import { createElement, useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { putNoBody } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useFilterState } from "@common/hooks/useFilterState";
import { getEnumValues } from "@common/enumOperations";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types";
import { RiskObservationRow, RiskObservationStatus, RiskObservationViewModel } from "./RiskObservations.types";
import { RiskObservationSaveModel, RiskObservationSource } from "./riskObservationDialog/RiskObservationDialog.types";
import { useFeatureFlags, usePlanContext } from "../../auth/planProvider/PlanProvider";
import { GetIncidentLogPlanLimit } from "../../auth/planProvider/PlanProvider.hooks";
import { FeatureFlag } from "../../auth/planProvider/PlanProvider.types";
import { usePanes } from "../../main/panes/usePanes";
import AddIncidentLogPane from "../../incidentLog/addIncidentLogPane/AddIncidentLogPane";
import { usePermissionsProvider } from "../../auth/usePermissionsProvider";
import { hasUnseenObservations, useObservationsContext } from "@common/providers/observationsProvider/ObservationsProvider";
import { DotLegalTableState, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { getDisplayedRows } from "@common/tableSearch";
import { useCustomListExport } from "../../customLists/customListView/useCustomListExport";

const sourceFilterId = "source";
const statusFilterId = "status";
const observedDateFilterId = "observedDate";

export function toRow(observation: RiskObservationViewModel, translateStatus: (status: number) => string): RiskObservationRow {
    return {
        ...observation,
        observedByWithEmail: observation.observedByEmail ? `${observation.observedBy} (${observation.observedByEmail})` : observation.observedBy,
        statusText: translateStatus(observation.status),
    };
}

// Mirrors GET /observations/newest, which the top bar compares against
export function getNewestNewObservationCreated(observations: Array<RiskObservationViewModel> | undefined) {
    return observations
        ?.filter((observation) => observation.status === RiskObservationStatus.New)
        .reduce<
            string | undefined
        >((newest, observation) => (!newest || new Date(observation.created) > new Date(newest) ? observation.created : newest), undefined);
}

export function getObservationHeaders(translateString: (key: string) => string): Array<ITableHeader<RiskObservationRow>> {
    return [
        { property: "name", text: translateString("observation"), align: "left", showOnMobile: true },
        { property: "description", text: translateString("description"), align: "left", showOnMobile: true },
        { property: "source", text: translateString("source"), align: "left", showOnMobile: true },
        { property: "status", text: translateString("status"), align: "left", showOnMobile: true },
        { property: "observedByWithEmail", text: translateString("observedBy"), align: "left", showOnMobile: true },
        { property: "observedDate", text: translateString("observedDate"), align: "left", showOnMobile: true },
    ];
}

export function toSaveModel(observation: RiskObservationViewModel): RiskObservationSaveModel {
    return {
        id: observation.id,
        name: observation.name,
        description: observation.description,
        source: observation.source,
        observedBy: observation.observedBy,
        observedDate: new Date(observation.observedDate),
    };
}

export function useRiskObservationsDataMapping() {
    const queryClient = useDotLegalQueryClient();
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskObservationSource, translateRiskObservationStatus } = useTranslation();
    const { customerId, permissions } = useUserContext();
    const featureFlags = useFeatureFlags();
    const { dataProtectionFeatures } = usePlanContext();
    const incidentLogPlanLimit = GetIncidentLogPlanLimit();
    const { open: openPane } = usePanes();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const { markAsSeen: markObservationsAsSeen, newestCreated: polledNewestCreated } = useObservationsContext();
    const [showObservationDialog, setShowObservationDialog] = useState(false);
    const [observationForDeletion, setObservationForDeletion] = useState<RiskObservationViewModel | undefined>(undefined);
    const [observationForConvert, setObservationForConvert] = useState<RiskObservationViewModel | undefined>(undefined);
    const [observationForArchive, setObservationForArchive] = useState<RiskObservationViewModel | undefined>(undefined);
    const [selectedObservation, setSelectedObservation] = useState<RiskObservationSaveModel | undefined>(undefined);
    const [showPublicLinkDialog, setShowPublicLinkDialog] = useState(false);
    const [searchText, setSearchText] = useState("");
    const { exportObservationsInBackground } = useCustomListExport();

    const filterKey = `risk-observations-overview-${customerId}`;
    const {
        selectFilters,
        dateFilters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        sort: storedSort,
        onSortChange,
    } = useFilterState(filterKey);
    const sort = storedSort ?? { orderBy: "observedDate", order: "desc" };

    // First time the inbox is opened this session, default to showing only New observations. Once the user
    // changes or clears the filter, the persisted snapshot takes over and we never re-seed.
    useEffect(() => {
        if (sessionStorage.getItem(filterKey) === null) {
            onFilterChange([
                { id: statusFilterId, label: translateString("status"), type: FilterOptionType.SelectField, value: [RiskObservationStatus.New.toString()] },
            ]);
        }
        // Run once per mount; the sessionStorage check guards against re-seeding.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const url = "/observations";
    const { isLoading, data } = useDotLegalQuery<Array<RiskObservationViewModel>>(url);

    // The list shows the newest observations, including ones the user just created, so the top bar stops marking them
    const newestNewObservationCreated = getNewestNewObservationCreated(data);
    useEffect(() => {
        if (newestNewObservationCreated) {
            markObservationsAsSeen(newestNewObservationCreated);
        }
    }, [newestNewObservationCreated, markObservationsAsSeen]);

    // A public report can arrive while the page is open. Only the top bar polls, so without this the dot would light up
    // on the page the user is already on, and the list would not show the new observation until a reload
    const isListBehindPoll = !isLoading && hasUnseenObservations(polledNewestCreated, newestNewObservationCreated ?? null);
    useEffect(() => {
        if (isListBehindPoll) {
            queryClient.refetchQueries(url);
        }
    }, [isListBehindPoll, polledNewestCreated, queryClient]);

    const filteredData = applyClientFilters(
        data,
        [
            { id: sourceFilterId, candidate: (observation) => observation.source.toString() },
            { id: statusFilterId, candidate: (observation) => observation.status.toString() },
        ],
        selectFilters,
        {},
        filterOperators,
        (observation) => matchesDateFilter(dateFilters[observedDateFilterId], observation.observedDate)
    );

    const rows = filteredData?.map((observation) => toRow(observation, translateRiskObservationStatus));

    const hasObservations = isLoading || (data?.length ?? 0) > 0;

    const sourceOptions = getEnumValues(RiskObservationSource).map((source) => ({ id: source.toString(), name: translateRiskObservationSource(source) }));
    const statusOptions = getEnumValues(RiskObservationStatus).map((status) => ({ id: status.toString(), name: translateRiskObservationStatus(status) }));

    const getFilterDataOptions = (): Array<FilterOption> => {
        return withOperators(
            [
                {
                    type: FilterOptionType.SelectField,
                    id: sourceFilterId,
                    value: selectFilters[sourceFilterId] ?? [],
                    label: translateString("source"),
                    fixedData: sourceOptions,
                },
                {
                    type: FilterOptionType.SelectField,
                    id: statusFilterId,
                    value: selectFilters[statusFilterId] ?? [],
                    label: translateString("status"),
                    fixedData: statusOptions,
                },
                {
                    type: FilterOptionType.DateField,
                    id: observedDateFilterId,
                    value: dateFilters[observedDateFilterId] ?? "",
                    label: translateString("observedDate"),
                    hideFutureDates: true,
                },
            ],
            filterOperators
        );
    };

    const onSave = () => {
        queryClient.refetchQueries(url);
    };

    // The table sorts internally and only reports it back, so the export can order its rows the same way. It also
    // emits onChange on page changes with the unchanged sort, so only a genuine sort change is stored.
    const onTableChange = (state: DotLegalTableState<RiskObservationRow>) => {
        if (!state.property) {
            return;
        }

        const orderBy = String(state.property);
        if (orderBy !== sort.orderBy || state.order !== sort.order) {
            onSortChange({ orderBy, order: state.order });
        }
    };

    const onExportClick = async () => {
        const headers = getObservationHeaders(translateString).map((header) => ({ header: header.text, column: String(header.property) }));
        const displayedIds = getDisplayedRows(rows ?? [], searchText, sort.orderBy, sort.order).map((row) => row.id);

        await exportObservationsInBackground(translateString("observations"), headers, displayedIds);

        snackbar.show(translateString("observationDataExportInProgress"), "success", true);
    };

    const canCreate = permissions.observationPermissions.create;
    const canEdit = permissions.observationPermissions.edit;
    const canDelete = permissions.observationPermissions.delete;

    // Observations are no longer part of the risk module, but a risk scenario still is
    const canConvertToRiskScenario = canEdit && hasEnterpriseRiskManagementAccess && permissions.canManageMasterData;

    // Converting lands in the incident steps, which only exist behind the incident log templates flag
    const canConvertToIncident = canEdit && featureFlags.isEnabled(FeatureFlag.IncidentLogTemplates) && permissions.incidentLogPermissions.create;

    const isIncidentLimitReached = incidentLogPlanLimit.data?.limitReached ?? false;
    const incidentLimitReachedText = translateString("incidentLogLimitReached", { count: dataProtectionFeatures.maxNumberOfIncidentLogEntries });

    const convertToIncident = (observation: RiskObservationViewModel) => {
        openPane(AddIncidentLogPane, { observation: { id: observation.id, name: observation.name, description: observation.description } });
    };

    const reactivateMutation = useDotLegalMutation((observationId: string) => putNoBody<string>(`/observations/${observationId}/reactivate`));

    const reactivateObservation = (observation: RiskObservationViewModel) => {
        reactivateMutation.mutateAsync(observation.id, {
            onSuccess: (response: Result<string>) => {
                if (response.success()) {
                    onSave();
                    snackbar.show(createElement(Trans, { i18nKey: "observationReactivated", values: { name: observation.name } }));
                }
            },
        });
    };

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        data: rows,
        hasObservations,
        getFilterDataOptions,
        mapSearchFilter: onFilterChange,
        showObservationDialog,
        setShowObservationDialog,
        observationForDeletion,
        setObservationForDeletion,
        observationForConvert,
        setObservationForConvert,
        observationForArchive,
        setObservationForArchive,
        selectedObservation,
        setSelectedObservation,
        onSave,
        reactivateObservation,
        searchText,
        setSearchText,
        sort,
        onTableChange,
        onExportClick,
        canCreate,
        canEdit,
        canDelete,
        canConvertToRiskScenario,
        canConvertToIncident,
        isIncidentLimitReached,
        incidentLimitReachedText,
        convertToIncident,
        showPublicLinkDialog,
        setShowPublicLinkDialog,
    };
}
